import { useActionsTrack } from "@/_old/features/analytics";
import { Button } from "@/core/components/ui/button";
import { Checkbox } from "@/core/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { Label } from "@/core/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { productTypeNames } from "@/core/utils";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface SpaceTypeFilterProps extends React.ComponentPropsWithoutRef<
  typeof Popover
> {
  location: string;
  onSubmit?: boolean;
  onChange?: (value: string[] | null) => void;
}

export function SpaceTypeFilter({
  location,
  onSubmit,
  onChange,
  ...props
}: SpaceTypeFilterProps) {
  const { t } = useTranslation(["dashboard"]);
  const { onActionClick } = useActionsTrack();
  const { filters, setFilters } = useDashboardFilters();
  const [selectedItem, setSelectedItem] = useState<
    | {
        id: string;
        name: string;
      }[]
    | null
  >(
    filters.type?.map((type) => ({
      id: type,
      name: t(productTypeNames[type]) as string,
    })) || null,
  );

  const handleChange = useCallback(
    (value: { id: string; name: string }[] | null) => {
      onActionClick("INSIGHTS_USE_FILTER_SPACE_TYPE", "DASHBOARD");

      const spaceTypeIds = value
        ? value.map((spaceType) => spaceType.id).filter(Boolean)
        : [];

      if (onSubmit) {
        onChange?.(spaceTypeIds as string[]);
      } else {
        setFilters({ type: spaceTypeIds as string[] });
      }
    },
    [onChange, onSubmit, setFilters],
  );

  const handleSelect = useCallback(
    (spaceType: { id: string; name: string } | null) => {
      if (spaceType === null) {
        setSelectedItem(null);
        handleChange(null);
      } else {
        setSelectedItem((prev) => {
          if (!prev) {
            const newSelection = [spaceType];
            handleChange(newSelection);
            return newSelection;
          }

          const isAlreadySelected = prev.some(
            (selectedSpaceType) => selectedSpaceType.id === spaceType.id,
          );

          if (isAlreadySelected) {
            const newSelection = prev.filter(
              (selectedSpaceType) => selectedSpaceType.id !== spaceType.id,
            );
            handleChange(newSelection.length > 0 ? newSelection : null);
            return newSelection.length > 0 ? newSelection : null;
          } else {
            const newSelection = [...prev, spaceType];
            handleChange(newSelection);
            return newSelection;
          }
        });
      }
    },
    [handleChange],
  );

  const data: { id: string; name: string }[] = Object.entries(
    productTypeNames,
  ).map(([key, type]) => ({
    id: key,
    name: t(type) as string,
  }));

  useEffect(() => {
    setSelectedItem(
      filters.type?.map((type) => ({
        id: type,
        name: t(productTypeNames[type]) as string,
      })) || null,
    );
  }, [filters.type]);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`${location}-space-type-filter`}>
        {t("space-type-filter-label") as string}
      </Label>

      <Popover {...props}>
        <PopoverTrigger asChild>
          <Button
            id={`${location}-space-type-filter`}
            variant="outline"
            className="justify-between w-full bg-white"
          >
            <span className="overflow-hidden font-normal text-ellipsis">
              {selectedItem && selectedItem.length > 0
                ? selectedItem.length === 1
                  ? `${selectedItem[0].name}`
                  : (t("space-type-filter-selected-multiple", {
                      count: selectedItem.length,
                    }) as string)
                : (t("space-type-filter-placeholder") as string)}
            </span>
            <ChevronDown className="opacity-50 size-4" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="p-0 w-[500px] max-w-[500px]">
          <Command>
            <CommandInput
              placeholder={t("space-type-filter-search-placeholder") as string}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>{t("space-type-filter-empty-state")}</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value=""
                  onSelect={() => {
                    handleSelect(null);
                  }}
                >
                  <Checkbox
                    className="mr-2"
                    checked={!selectedItem || selectedItem.length === 0}
                  />

                  {t("space-type-filter-placeholder")}
                </CommandItem>
                {data.map((spaceType, index) => (
                  <CommandItem
                    key={`${spaceType.id}-${index}`}
                    value={spaceType.name}
                    onSelect={() => {
                      handleSelect(spaceType);
                    }}
                  >
                    <Checkbox
                      className="mr-2"
                      checked={
                        selectedItem?.some(
                          (selectedMember) =>
                            selectedMember.id === spaceType.id,
                        ) ||
                        selectedItem?.length === 0 ||
                        !selectedItem
                      }
                    />

                    {spaceType.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
